import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Search, DollarSign, ChevronLeft, ChevronRight, Package, Download,
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 50;
const IVA_MAP = { 1: 4, 2: 10, 3: 21 };
const getIvaPercent = (tipoIva) => IVA_MAP[tipoIva] || 21;

const ComercialPreciosCliente = () => {
  const { clientId } = useParams();
  const { token, API_URL } = useAuth();

  const [clientName, setClientName] = useState('');
  const [prices, setPrices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  // Fetch client info
  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/clients/${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setClientName(data.razon_social || data.alias || `Cliente #${clientId}`);
        }
      } catch {
        setClientName(`Cliente #${clientId}`);
      }
    })();
  }, [API_URL, token, clientId]);

  // Fetch prices
  const fetchPrices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(ITEMS_PER_PAGE),
      });
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      const res = await fetch(`${API_URL}/prices/client/${clientId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error cargando precios');
      const data = await res.json();
      setPrices(data.prices || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching prices:', err);
      setPrices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, clientId, page, searchTerm]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setSearchTerm(val);
      setPage(1);
    }, 400));
  };

  // Descarga Excel con todos los precios del cliente
  const handleExportExcel = async () => {
    try {
      const res = await fetch(`${API_URL}/prices/client/${clientId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error exportando');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `precios_${(clientName || 'cliente').replace(/\s+/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar Excel: ' + err.message);
    }
  };

  // Descarga PDF con los precios del cliente (replica generate_pdf.php legacy)
  const handleExportPdf = async () => {
    try {
      const res = await fetch(`${API_URL}/prices/client/${clientId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error exportando PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `precios_${(clientName || 'cliente').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar PDF: ' + err.message);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Link
            to="/comercial/clientes"
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            title="Volver a Mis Clientes"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </Link>
          <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
            <DollarSign className="w-6 h-6" style={{ color: PRIMARY }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Precios del Cliente</h1>
            <p className="text-sm text-gray-500">{clientName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download size={14} />
            Exportar Excel
          </button>
          <button
            onClick={handleExportPdf}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download size={14} />
            Exportar PDF
          </button>
          <span className="text-sm text-gray-400 ml-2">{total} artículos</span>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre de producto o referencia..."
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            style={{ focusRingColor: PRIMARY }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Venta (PVP)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">IVA %</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PVP + IVA</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: PRIMARY }} />
                  </td>
                </tr>
              ) : prices.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No se encontraron precios para este cliente</p>
                  </td>
                </tr>
              ) : (
                prices.map((p) => {
                  const ivaPercent = getIvaPercent(p.tipo_iva);
                  const pvpIva = Number(p.precio || 0) * (1 + ivaPercent / 100);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                        {p.producto_raiz || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {p.producto_nombre || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {Number(p.precio || 0).toFixed(2)} €
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                        {ivaPercent}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        {pvpIva.toFixed(2)} €
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, total)} de {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-700 font-medium px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-md border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComercialPreciosCliente;
